import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

export interface IDeviationEvent {
	DeviationEventID: number;
	AssetDistributionID: number;
	DatapointID: number;
	AcceptableDeviation: number;
	AssetID: number;
	ZScore: number;
	Sensitivity: number;
	PercentageDeviated: number;
	Timestamp: number;
	Acknowledged: boolean;
	EncodedImage?: string;
	AssetName?: string;
	AssetDescription?: string;
	Message?: string;
	DPTitle?: string;
	AcknowledgedUser?: number;
	AcknowledgedTimestamp?: number;
	ModelType?: number;
}

export class DeviationEvent implements IDeviationEvent {
	/**
	 * Default Constructor
	 * @param DeviationEventID DeviationEventID  
	 * @param AssetDistributionID AssetDistributionID 
	 * @param DatapointID DatapointID 
	 * @param AcceptableDeviation AcceptableDeviation 
	 * @param AssetID AssetID 
	 * @param ZScore ZScore 
	 * @param Sensitivity Sensitivity 
	 * @param PercentageDeviated PercentageDeviated 
	 * @param Timestamp Timestamp 
	 * @param EncodedImage EncodedImage 
	 * @param AssetName AssetName 
	 * @param AssetDescription AssetDescription 
	 * @param Message Message 
	 * @param DPTitle DPTitle 
	 */
	constructor(
		public DeviationEventID: number,
		public AssetDistributionID: number,
		public DatapointID: number,
		public AcceptableDeviation: number,
		public AssetID: number,
		public ZScore: number,
		public Sensitivity: number,
		public PercentageDeviated: number,
		public Timestamp: number,
		public Acknowledged: boolean,
		public EncodedImage?: string,
		public AssetName?: string,
		public AssetDescription?: string,
		public Message?: string,
		public DPTitle?: string,
		public AcknowledgedUser?: number,
		public AcknowledgedTimestamp?: number,
		public ModelType?: number
	) { }
}

/** ITimelineItem */
export interface ITimelineItem {
	/** Category */
	Category: number;
	/** Content */
	Content: string;
	/** Timestamp */
	Timestamp: number;
}

/** TimelineItem */
export class TimelineItem implements ITimelineItem {

	/**
	 * Default Constructor
	 * @param Category Category  
	 * @param Content Content 
	 * @param Timestamp Timestamp  
	 */
	constructor(
		public Category: number,
		public Content: string,
		public Timestamp: number
	) { }
}

/** IEventComment - comment type for all events */
export interface IEventComment {
	/** Rule Event ID */
	EventID: number;
	/** Comment Text */
	CommentText: string;
	/** Rule Event Comment ID */
	EventCommentID?: number;
	/** Timestamp */
	Timestamp?: number;
	/** AddedByUserID */
	AddedByUserID?: string;
	/** EventType */
	EventType: number;
}

/** EventComment - comment type for all events */
export class EventComment implements IEventComment {

	/**
	 * Default Constructor
	 * @param EventID 
	 * @param CommentText 
	 * @param Timestamp 
	 * @param AddedByUserID 
	 * @param EventCommentID 
	 */
	constructor(
		public EventID: number,
		public CommentText: string,
		public EventType: number,
		public Timestamp?: number,
		public AddedByUserID?: string,
		public EventCommentID?: number
	) { }
}

/** IWebhookEvent */
export interface IWebhookEvent {
	/** WebhookEventID */
	WebhookEventID: number;
	/** WebhookID */
	WebhookID: number;
	/** WebhookClientID */
	ClientWebhookID: string;
	/** WebhookUID */
	WebhookUID: string;
	/** Description */
	Description: string;
	/** WebhookType */
	WebhookType: number;
	/** WebhookEventType */
	WebhookEventType: number;
	/** TimeStamp */
	TimeStamp: number;
	/** Subscribers */
	Subscribers?: string;
	/** WebhookEventText */
	WebhookEventText?: string;
}

/** WebhookEvent */
export class WebhookEvent implements IWebhookEvent {
	/**
	 * Default Constructor
	 * @param WebhookEventID 
	 * @param WebhookID 
	 * @param ClientWebhookID 
	 * @param WebhookUID 
	 * @param Description 
	 * @param WebhookType 
	 * @param WebhookEventType 
	 * @param TimeStamp 
	 * @param Subscribers 
	 * @param WebhookEventText 
	 */
	constructor(
		public WebhookEventID: number,
		public WebhookID: number,
		public ClientWebhookID: string,
		public WebhookUID: string,
		public Description: string,
		public WebhookType: number,
		public WebhookEventType: number,
		public TimeStamp: number,
		public Subscribers?: string,
		public WebhookEventText?: string
	) { }
}

/** IOfflineEvent */
export interface IOfflineEvent {

	/** OfflineEventID */
	OfflineEventID: number;

	/** SensorID */
	SensorID: number;

	/** SensorName */
	SensorName: string;

	/** OfflineEventText */
	OfflineEventText: string;

	/** TimeStamp */
	TimeStamp: number;

	/** Acknowledged */
	Acknowledged: boolean;

	/** AcknowledgedUser */
	AcknowledgedUser: number;

	/** AcknowledgedTimestamp */
	AcknowledgedTimestamp: number;

	/** Subscribers */
	Subscribers?: string;

	/** AssetIDs */
	AssetIDs?: string;

	/** SensorEventType */
	SensorEventType?: number;
}

/** OfflineEvent */
export class OfflineEvent implements IOfflineEvent {

	/**
	 * Default Constructor
	 * @param OfflineEventID 
	 * @param SensorID 
	 * @param SensorName 
	 * @param OfflineEventText 
	 * @param TimeStamp 
	 * @param Acknowledged 
	 * @param Subscribers 
	 */
	constructor(
		public OfflineEventID: number,
		public SensorID: number,
		public SensorName: string,
		public OfflineEventText: string,
		public TimeStamp: number,
		public Acknowledged: boolean,
		public AcknowledgedUser: number,
		public AcknowledgedTimestamp: number,
		public Subscribers?: string,
		public AssetIDs?: string,
		public SensorEventType?: number
	) { }
}

export interface IRuleEvent {

	/** RuleEventID */
	RuleEventID: number;

	/** RuleID */
	RuleID: number;

	/** RuleText */
	RuleText: string;

	/** RuleValues */
	RuleValues: string;

	/** TimeStamp */
	TimeStamp: number;

	/** AssetName */
	AssetName: string;

	/** RuleName */
	RuleName: string;

	/** Acknowledged */
	Acknowledged: boolean

	/** AssetID */
	AssetID: number

	/** Archived */
	Archived: boolean

	/** AcknowledgedUser */
	AcknowledgedUser: number;

	/** AcknowledgedTimestamp  */
	AcknowledgedTimestamp: number;

	/** TimelineID */
	TimelineID: number;

	/** Comments */
	Comments: string;

	CaseID: string;

}

export class RuleEvent implements IRuleEvent {

	/**
	 * RuleEvent
	 * @param RuleEventID RuleEventID  
	 * @param RuleID RuleID 
	 * @param RuleText RuleText 
	 * @param RuleValues RuleValues 
	 * @param TimeStamp TimeStamp 
	 * @param AssetName AssetName 
	 * @param RuleName RuleName 
	 * @param Acknowledged Acknowledged 
	 * @param AssetID AssetID 
	 * @param Archived Archived 
	 * @param AcknowledgedUser AcknowledgedUser 
	 * @param AcknowledgedTimestamp AcknowledgedTimestamp 
	 * @param TimelineID TimelineID 
	 */
	constructor(
		public RuleEventID: number,
		public RuleID: number,
		public RuleText: string,
		public RuleValues: string,
		public TimeStamp: number,
		public AssetName: string,
		public RuleName: string,
		public Acknowledged: boolean,
		public AssetID: number,
		public Archived: boolean,
		public AcknowledgedUser: number,
		public AcknowledgedTimestamp: number,
		public TimelineID: number,
		public Comments: string,
		public CaseID: string
	) { }
}

/**
 * Injectable
 */
@Injectable({
	providedIn: 'root'
})
export class EventsDataService {

	/** apiUnits */
	private apiEvents = "api/{0}/organisation/{1}/Events";

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions Global Functions
	 * @param siteSubscriptions site Subscriptions
	 */
	constructor(
		private http: HttpClient,
		public globalFunctions: GlobalFunctions,
		public siteSubscriptions: siteSubscriptions
	) { }


	/**
	 * Get offline events
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param offlineEventID 
	 * @param timeStampTo 
	 * @param timeStampFrom 
	 * @returns 
	 */
	getOfflineEvents$(AccountGUID: string, orgUID: string, offlineEventID?: number, timeStampTo?: number, timeStampFrom?: number): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/OfflineEvents`;

		if (offlineEventID) {
			url += `?offlineEventID=${offlineEventID}`;
			if (timeStampFrom && timeStampTo) {
				url += `&timeStampFrom=${timeStampFrom}&timeStampTo=${timeStampTo}`
			}
		}
		if (timeStampFrom && timeStampTo && !offlineEventID) {
			url += `?timeStampFrom=${timeStampFrom}&timeStampTo=${timeStampTo}`
		}
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Get webhook events
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param webhookEventID 
	 * @param timeStampTo 
	 * @param timeStampFrom 
	 * @returns 
	 */
	getWebhookEvents$(AccountGUID: string, orgUID: string, webhookEventID?: number, timeStampTo?: number, timeStampFrom?: number): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/WebhookEvents`;

		if (webhookEventID) {
			url += `?webhookEventID=${webhookEventID}`;
			if (timeStampFrom && timeStampTo) {
				url += `&timeStampFrom=${timeStampFrom}&timeStampTo=${timeStampTo}`
			}
		}
		if (timeStampFrom && timeStampTo && !webhookEventID) {
			url += `?timeStampFrom=${timeStampFrom}&timeStampTo=${timeStampTo}`
		}
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Get rule events
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param ruleID 
	 * @param ruleEventID 
	 * @param timeStampFrom 
	 * @param timeStampTo 
	 * @returns 
	 */
	getRuleEvents$(AccountGUID: string, orgUID: string, ruleID?: number, ruleEventID?: number, timeStampFrom?: number, timeStampTo?: number): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/RuleEvents/${ruleID}`;
		if (ruleEventID) {
			url += `?ruleEventID=${ruleEventID}`;
			if (timeStampFrom && timeStampTo) {
				url += `&timeStampFrom=${timeStampFrom}&timeStampTo=${timeStampTo}`
			}
		}
		if (timeStampFrom && timeStampTo && !ruleEventID) {
			url += `?timeStampFrom=${timeStampFrom}&timeStampTo=${timeStampTo}`
		}
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * getDeviationEvents
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param assetID assetID 
	 * @param deviationEventID deviationEventID 
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 * @returns 
	 */
	getDeviationEvents$(AccountGUID: string, orgUID: string, assetID?: number, deviationEventID?: number,
		timeStampFrom?: number, timeStampTo?: number, modelType?: number, modelID?: number): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/DeviationEvents`;
		if (deviationEventID) {
			url += `?deviationEventID=${deviationEventID}`;
			if (timeStampFrom && timeStampTo) {
				url += `&timeStampFrom=${timeStampFrom}&timeStampTo=${timeStampTo}`;
			}
		}
		if (timeStampFrom && timeStampTo && !deviationEventID) {
			url += `?timeStampFrom=${timeStampFrom}&timeStampTo=${timeStampTo}`;
		}
		if (modelType && modelID) {
			url += `?modelType=${modelType}&modelID=${modelID}`;
		}
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Archive a rule event
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param ruleEventID ruleEventID 
	 * @returns 
	 */
	archiveRuleEvent$(AccountGUID: string, orgUID: string, ruleEventID: number,): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/RuleEvents/Archive/${ruleEventID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Reopen a rule event
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param ruleEventID ruleEventID 
	 * @returns 
	 */
	reopenRuleEvent$(AccountGUID: string, orgUID: string, ruleEventID: number,): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/RuleEvents/Reopen/${ruleEventID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Get Multiple Rule Events
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param ruleEventIDs ruleEventIDs 
	 * @returns 
	 */
	getRuleEventsMultiple$(AccountGUID: string, orgUID: string, ruleEventIDs: string): Observable<any> {
		let data = { ruleEventIDs };

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/RuleEventsMultiple`;
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Posts an event comment
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param eventID 
	 * @param comment 
	 * @param eventType 
	 * @returns 
	 */
	postEventComment$(AccountGUID: string, orgUID: string, eventID: number, comment: EventComment, eventType: number, userID: number): Observable<any> {
		let data = JSON.stringify(comment);

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/eventComments/${eventID}`;

		if (eventType) {
			url += `?eventType=${eventType}`
		}
		if (userID) {
			url += `&userID=${userID}`
		}
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Gets all event comments
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param eventID 
	 * @param eventType 
	 * @returns 
	 */
	getEventComment$(AccountGUID: string, orgUID: string, eventID: number, eventType?: number): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/eventComments/${eventID}`;

		if (eventType) {
			url += `?eventType=${eventType}`
		}
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * Patches an event comment
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param eventComment 
	 * @param eventType 
	 * @returns 
	 */
	patchEventComment$(AccountGUID: string, orgUID: string, eventComment: EventComment, eventType?: number): Observable<any> {
		let data = JSON.stringify(eventComment);

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/eventComments/${eventComment.EventCommentID}`;

		if (eventType) {
			url += `?eventType=${eventType}`
		}
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * Deleted an event comment
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param eventCommentID 
	 * @param eventType 
	 * @returns 
	 */
	deleteEventComment$(AccountGUID: string, orgUID: string, eventCommentID?: number, eventType?: number): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/eventComments/${eventCommentID}`;

		if (eventType) {
			url += `?eventType=${eventType}`
		}
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * Acknowledge Sensor Event For All Assets
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param sensorID sensorID 
	 * @param eventID eventID 
	 * @returns 
	 */
	acknowledgeSensorEventForAllAssets$(AccountGUID: string, orgUID: string, sensorID: number, eventID: number): Observable<any> {
		let data = {}

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/acknowledgeSensorEvent/${eventID}/sensor/${sensorID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * getTimeline
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @param timelineID timelineID 
	 * @returns 
	 */
	getTimeline$(AccountGUID: string, orgUID: string, timelineID: number): Observable<any> {
		let data = {};

		let url = this.apiEvents;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/Timeline/${timelineID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}
}
